import React, { useEffect, useState, useRef  } from 'react';
import mapboxgl from 'mapbox-gl';
import MapDrawer from './MapDrawer';
import MapInfo from './MapInfo';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Progress } from 'antd';

const MapCountries = ({ apiUrl }) => {
    const [map, setMap] = useState(null);
    const [items, setItems] = useState({});
    const [visible, setVisible] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const foundCountryColor = '#009bdf';
    const containerRef = useRef(null);
    const [context, setContext] = useState(null);
    const uicregionCodes = {
        'Africa':'AF',
        'Asia Pacific':'AS',
        'Europe':'EU',
        'Middle-East':'ME',
        'North America':'NA',
        'Latin America':'LA' 
    };
    
    const queryParams = new URLSearchParams(window.location.search);
    const uicregion = queryParams.get('uicregion');

    useEffect(() => {
        mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
        const newMap = new mapboxgl.Map({
            container: 'map-countries',
            style: 'mapbox://styles/aymericb/ckmnuxe1e0yis17o6pier7xyo',
            center: [0, 20],
            zoom: 2
        });

        newMap.on('load', () => {
            newMap.addSource('countries', {
                type: 'vector',
                url: 'mapbox://mapbox.country-boundaries-v1'
            });
            setMap(newMap);
        });

        return () => newMap && newMap.remove();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            if (uicregion) {
                apiUrl = `${apiUrl}/${uicregionCodes[uicregion]}`;
                console.log('apiUrl:', apiUrl);  // Debugging URL
                setContext(uicregion);
            }
            try {
                const response = await fetch(apiUrl);
                const data = await response.json();
                const groupedData = data.reduce((acc, item) => {
                    // Ajout de la condition sur uic_region si uicregion est spécifié
                    if (!uicregion || item.uic_region.includes(uicregion)) {
                        acc[item.country_iso3] = acc[item.country_iso3] || [];
                        acc[item.country_iso3].push(item);
                    }
                    return acc;
                }, {});
                setItems(groupedData);
            } catch (error) {
                console.error('Error fetching data:', error);
                setError('Failed to load data');
            }
            setLoading(false);
        };

        if (map) {
            fetchData();
        }
    }, [apiUrl, map, uicregion]);

    useEffect(() => {
        if (!map || !Object.keys(items).length) return;
        
        map.addLayer({
            id: 'countries-fill',
            type: 'fill',
            source: 'countries',
            'source-layer': 'country_boundaries',
            'generateId': true,
            paint: {
                'fill-color': [
                    'match',
                    ['get', 'iso_3166_1_alpha_3'],
                    ...Object.keys(items).flatMap(key => [key, foundCountryColor]),
                    '#bbbbbb'
                ],
                'fill-opacity': [
                    'case',
                    ['boolean', ['feature-state', 'hover'], false],
                    0.5,
                    1
                ]
            }
        });

        map.on('mousemove', 'countries-fill', (e) => {
            const featureId = e.features[0].id;
            map.setFeatureState(
                { source: 'countries', sourceLayer: 'country_boundaries', id: featureId },
                { hover: true }
            );
        });

        map.on('mouseleave', 'countries-fill', () => {
            if (map.getLayer('countries-fill')) {
                map.removeFeatureState({ source: 'countries', sourceLayer: 'country_boundaries' });
            }
        });

        map.on('click', 'countries-fill', (e) => {
            const countryCode = e.features[0].properties.iso_3166_1_alpha_3;
            const countryItems = items[countryCode] || [];
            if (countryItems.length > 0) {
                setSelectedCountry({
                    countryIso3: countryCode,
                    countryName: countryItems[0].country_name,
                    items: countryItems
                });
                setVisible(true);
            }
        });

    }, [map, items]);

    return (
        <div ref={containerRef} id="full-screen-container" style={{ width: '100vw', height: '100vh', position: 'relative' }}>
            {loading && <Progress style={{ position: 'absolute' }} />}
            <div id="map-countries" style={{ width: '100vw', height: '100vh' }}></div>
            {!loading && !error && (
                    <> 
                        <MapDrawer isOpen={visible} onClose={() => setVisible(false)} title={selectedCountry?.countryName} items={selectedCountry?.items || []} />
                        <MapInfo context={context} />
                    </>
                )}
                {error && <div>Error: {error}</div>}
        </div>
    );
};

export default MapCountries;